/*
 * The game reads keys by where they are on the keyboard (KeyboardEvent.code: the camera's
 * WASD and Q/E, R to turn, M, K…), so on an AZERTY or QWERTZ keyboard the key to press carries
 * another letter. Where the browser tells (navigator.keyboard, Chromium and the desktop app),
 * the texts name the keys as printed on the player's keyboard: "WASD" reads "ZQSD" on AZERTY.
 * Elsewhere they keep the QWERTY letters.
 */

/** What is printed on each key the game uses, by code (null: not known, read as QWERTY). */
let printed: Map<string, string> | null = null;

interface LayoutMap {
  get(code: string): string | undefined;
}

/** Reads the keyboard's layout (once, at start; resolves at once where it cannot be read). */
export async function readKeyboardLayout(): Promise<void> {
  const keyboard = (navigator as { keyboard?: { getLayoutMap?: () => Promise<LayoutMap> } })
    .keyboard;
  if (!keyboard?.getLayoutMap) return;
  try {
    const map = await keyboard.getLayoutMap();
    const next = new Map<string, string>();
    let differs = false;
    for (let c = 65; c <= 90; c++) {
      const letter = String.fromCharCode(c);
      const label = map.get(`Key${letter}`)?.toUpperCase();
      if (!label) continue;
      next.set(letter, label);
      if (label !== letter) differs = true;
    }
    printed = differs ? next : null;
  } catch {
    // Not allowed here (a frame, a policy): QWERTY letters.
  }
}

const key = (letter: string) => printed?.get(letter) ?? letter;

/** A text with its key names as printed on the player's keyboard. */
export function localKeys(text: string): string {
  if (!printed) return text;
  return text
    .replace(/\bWASD\b/g, () => `${key('W')}${key('A')}${key('S')}${key('D')}`)
    .replace(/\bQ\s?\/\s?E\b/g, () => `${key('Q')}/${key('E')}`)
    .replace(/\(([A-Z])\)/g, (_, letter: string) => `(${key(letter)})`);
}
